import * as monaco from 'monaco-editor';

// shared model registry — models persist across TextEditorPanel mounts
// so undo history is kept when switching tabs
const models = new Map<string, monaco.editor.ITextModel>();

export const getOrCreateModel = (
	path: string,
	content: string,
	language: string,
): monaco.editor.ITextModel => {
	let model = models.get(path);
	if (model && !model.isDisposed()) return model;

	const uri = monaco.Uri.parse(`sftp://${path}`);

	// register the language with Monaco if not already known
	const known = monaco.languages.getLanguages().some((l) => l.id === language);
	if (!known) monaco.languages.register({ id: language });

	model = monaco.editor.createModel(content, language, uri);
	models.set(path, model);
	return model;
};

export const disposeModel = (path: string) => {
	const model = models.get(path);
	if (model && !model.isDisposed()) model.dispose();
	models.delete(path);
};

export const hasModel = (path: string): boolean => {
	const model = models.get(path);
	return model !== undefined && !model.isDisposed();
};

// maps file extensions to shiki/TextMate language IDs
const languageMap: Record<string, string> = {
	// web
	ts: 'typescript',
	tsx: 'tsx',
	mts: 'typescript',
	cts: 'typescript',
	js: 'javascript',
	jsx: 'jsx',
	mjs: 'javascript',
	cjs: 'javascript',
	json: 'json',
	jsonc: 'jsonc',
	json5: 'json5',
	html: 'html',
	htm: 'html',
	css: 'css',
	scss: 'scss',
	less: 'less',
	sass: 'sass',
	styl: 'stylus',
	vue: 'vue',
	svelte: 'svelte',
	astro: 'astro',
	// markup / data
	md: 'markdown',
	mdx: 'mdx',
	yaml: 'yaml',
	yml: 'yaml',
	toml: 'toml',
	xml: 'xml',
	svg: 'xml',
	graphql: 'graphql',
	gql: 'graphql',
	// shell
	sh: 'shellscript',
	bash: 'shellscript',
	zsh: 'shellscript',
	fish: 'fish',
	ps1: 'powershell',
	bat: 'bat',
	cmd: 'bat',
	// systems
	c: 'c',
	h: 'c',
	cpp: 'cpp',
	cc: 'cpp',
	cxx: 'cpp',
	hpp: 'cpp',
	hxx: 'cpp',
	rs: 'rust',
	go: 'go',
	zig: 'zig',
	// jvm
	java: 'java',
	kt: 'kotlin',
	kts: 'kotlin',
	scala: 'scala',
	groovy: 'groovy',
	// scripting
	py: 'python',
	rb: 'ruby',
	php: 'php',
	pl: 'perl',
	pm: 'perl',
	lua: 'lua',
	r: 'r',
	jl: 'julia',
	// .net
	cs: 'csharp',
	fs: 'fsharp',
	vb: 'vb',
	// mobile
	swift: 'swift',
	dart: 'dart',
	m: 'objective-c',
	mm: 'objective-cpp',
	// functional
	ex: 'elixir',
	exs: 'elixir',
	erl: 'erlang',
	hs: 'haskell',
	ml: 'ocaml',
	clj: 'clojure',
	// config / devops
	dockerfile: 'dockerfile',
	tf: 'terraform',
	hcl: 'hcl',
	ini: 'ini',
	conf: 'ini',
	cfg: 'ini',
	nginx: 'nginx',
	// data
	sql: 'sql',
	prisma: 'prisma',
	proto: 'proto',
	// misc
	tex: 'latex',
	rst: 'rst',
	makefile: 'make',
	cmake: 'cmake',
	diff: 'diff',
	patch: 'diff',
	log: 'log',
};

// filenames without extensions
const filenameMap: Record<string, string> = {
	dockerfile: 'dockerfile',
	makefile: 'make',
	cmakelists: 'cmake',
	gemfile: 'ruby',
	rakefile: 'ruby',
	vagrantfile: 'ruby',
};

export const getLanguageForPath = (path: string): string => {
	const basename = (path.split('/').pop() ?? '').toLowerCase();

	// check filename map first (dockerfile, makefile, etc.)
	const byName = filenameMap[basename.replace(/\..*$/, '')];
	if (byName) return byName;

	// then extension
	const ext = basename.split('.').pop() ?? '';
	return languageMap[ext] ?? 'text';
};
